import { useCallback, useState } from 'react'

/**
 * Which vehicles are chosen for a report.
 *
 * Deliberately NOT in the URL, unlike the register's filters.
 *
 * The filters belong there because they describe a view somebody may want to
 * share or return to. A selection is a working set — it is built up over a minute
 * of clicking, it is consumed once, and putting 40 ids in the address bar would
 * make every checkbox a history entry and the URL unreadable. It survives paging
 * and filtering because it lives above the query, which is the part that actually
 * matters; it does not survive a reload, and should not.
 *
 * A Set, so toggling 600 vehicles stays O(1) per click rather than scanning an
 * array each time.
 */
export function useVehicleSelection() {
  const [selected, setSelected] = useState<ReadonlySet<number>>(new Set())

  const toggle = useCallback((vehicleId: number) => {
    setSelected((current) => {
      const next = new Set(current)
      if (!next.delete(vehicleId)) next.add(vehicleId)
      return next
    })
  }, [])

  /**
   * Adds or removes a whole page.
   *
   * Adds unless every id given is already selected, so the header checkbox on a
   * partly-selected page completes it rather than clearing it — clearing loses
   * work the user did on other pages, and is the more expensive mistake.
   */
  const toggleMany = useCallback((vehicleIds: number[]) => {
    setSelected((current) => {
      const next = new Set(current)
      const allPresent = vehicleIds.every((id) => next.has(id))

      for (const id of vehicleIds) {
        if (allPresent) next.delete(id)
        else next.add(id)
      }

      return next
    })
  }, [])

  const clear = useCallback(() => setSelected(new Set()), [])

  return { selected, toggle, toggleMany, clear }
}
